import React, { useState } from 'react';
import { Phone, Mail, Copy, AlertCircle, Trash2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import type { FormSubmission } from '../../types';
import SearchBar from './SearchBar';
import { searchEstimations } from '../../lib/searchUtils';

interface ClientListProps {
  submissions: FormSubmission[];
  onSelect: (submission: FormSubmission) => void;
  loading?: boolean;
  connectionError?: boolean;
  onRetry?: () => void;
  onDelete?: (id: string) => void;
}

interface DeleteConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
}

const DeleteConfirmation: React.FC<DeleteConfirmationProps> = ({ isOpen, onClose, onConfirm }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
        <h3 className="text-lg font-semibold mb-4">Confirmer la suppression</h3>
        <p className="text-gray-600 mb-6">
          Êtes-vous sûr de vouloir supprimer cette estimation ? Cette action est irréversible et supprimera également tous les commentaires et fichiers associés.
        </p>
        <div className="flex justify-end space-x-4">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800"
          >
            Annuler
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Supprimer définitivement
          </button>
        </div>
      </div>
    </div>
  );
};

export default function ClientList({ 
  submissions, 
  onSelect, 
  loading, 
  connectionError, 
  onRetry,
  onDelete 
}: ClientListProps) {
  const [displayCount, setDisplayCount] = React.useState(10);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setDisplayCount(10);
  };

  const filteredSubmissions = searchEstimations(submissions, searchQuery);
  const displayedSubmissions = filteredSubmissions.slice(0, displayCount);
  const hasMore = filteredSubmissions.length > displayCount;

  const unreadCount = submissions.filter(s => !s.viewed).length;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleCall = (phone: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.location.href = `tel:${phone}`;
  };

  const handleEmail = (email: string, e: React.MouseEvent) => {
    e.stopPropagation();
    window.location.href = `mailto:${email}`;
  };

  const handleCopyReference = (reference: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(reference);
    toast.success('Référence copiée dans le presse-papier');
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirmId(id);
  };

  const handleConfirmDelete = async () => {
    if (!deleteConfirmId || !onDelete) return;
    
    try {
      const response = await fetch(`/api/delete-estimation/${deleteConfirmId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error('Erreur lors de la suppression');
      }

      onDelete(deleteConfirmId);
      toast.success('Estimation supprimée avec succès');
    } catch (error) {
      console.error('Error deleting estimation:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-2 text-gray-500">Chargement des données...</p>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="p-8 text-center">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-gray-700 font-medium">Erreur de connexion au serveur</p>
        <p className="text-gray-500 mt-2">Veuillez vérifier votre connexion internet et réessayer</p>
        {onRetry && (
          <button
            onClick={onRetry}
            className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700"
          >
            Réessayer
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <SearchBar onSearch={handleSearch} />
        {unreadCount > 0 && (
          <div className="bg-indigo-600 text-white px-3 py-1 rounded-full text-sm font-medium">
            {unreadCount} nouvelle{unreadCount > 1 ? 's' : ''} estimation{unreadCount > 1 ? 's' : ''}
          </div>
        )}
      </div>
      
      <div className="bg-white shadow rounded-lg overflow-hidden max-h-[calc(100vh-200px)] flex flex-col">
        <div className="overflow-x-auto flex-1">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"></th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nom et prénom
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type de bien
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Référence
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {displayedSubmissions.map((submission) => (
                <tr
                  key={submission.id}
                  onClick={() => onSelect(submission)}
                  className="hover:bg-gray-50 cursor-pointer"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    {!submission.viewed && (
                      <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {submission.prenom} {submission.nom}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm text-gray-500">{submission.telephone}</span>
                      <button
                        onClick={(e) => handleCall(submission.telephone, e)}
                        className="text-green-500 hover:text-green-600"
                      >
                        <Phone className="h-4 w-4" />
                      </button>
                      <button
                        onClick={(e) => handleEmail(submission.email, e)}
                        className="text-blue-500 hover:text-blue-600"
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {submission.type_propriete}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {formatDate(submission.created_at)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-mono text-gray-500">{submission.id.slice(0, 8)}</span>
                      <button
                        onClick={(e) => handleCopyReference(submission.id, e)}
                        className="text-gray-400 hover:text-gray-600"
                        title="Copier la référence"
                      >
                        <Copy className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={(e) => handleDelete(submission.id, e)}
                      className="text-red-600 hover:text-red-800"
                      title="Supprimer l'estimation"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {hasMore && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <button
              onClick={() => setDisplayCount(prev => prev + 10)}
              className="w-full py-2 px-4 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Afficher plus d'estimations
            </button>
          </div>
        )}
      </div>

      <DeleteConfirmation
        isOpen={!!deleteConfirmId}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
}